import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { SLUG_COVERS, CATEGORY_IMAGES, ALL_IMAGES, hash, coverFor } from "@/lib/covers";

const categoryColors = {
  "Artificial Intelligence": "bg-violet-500/90 text-white",
  "Education": "bg-emerald-500/90 text-white",
  "Future of Work": "bg-blue-500/90 text-white",
  "Policy": "bg-amber-500/90 text-white",
  "Economy": "bg-rose-500/90 text-white",
  "Workforce": "bg-cyan-500/90 text-white",
};

const signals = [
  {
    category: "Artificial Intelligence",
    title: "AI Agents Move from Assistants to Economic Actors",
    date: "Oct 13, 2026",
    readTime: "5 min read",
    summary: "AI agents are beginning to perform economic roles with measurable value, signaling a shift in how work, productivity and...",
    slug: "ai-agents-economic-actors",
    image: SLUG_COVERS["ai-agents-economic-actors"],
  },
  {
    category: "Education",
    title: "Universities Rethink College ROI in the Age of AI",
    date: "Oct 13, 2026",
    readTime: "5 min read",
    summary: "New data shows growing momentum for skills-based, modular and hybrid pathways as traditional degree models face increasing pressure.",
    slug: "universities-rethink-roi",
    image: SLUG_COVERS["universities-rethink-roi"],
  },
  {
    category: "Future of Work",
    title: "The Entry-Level Career Ladder Is Being Rewritten",
    date: "Oct 12, 2026",
    readTime: "5 min read",
    summary: "As AI takes on more early-career tasks, organizations are redesigning talent development and what it means to 'start' a career.",
    slug: "entry-level-career-ladder",
    image: SLUG_COVERS["entry-level-career-ladder"],
  },
  {
    category: "Policy",
    title: "Global Governments Accelerate AI Workforce Strategies",
    date: "Oct 13, 2026",
    readTime: "6 min read",
    summary: "From the EU to Southeast Asia, governments are racing to develop workforce transformation policies before automation reshapes their economies.",
    slug: "governments-ai-workforce",
    image: SLUG_COVERS["governments-ai-workforce"],
  },
  {
    category: "Artificial Intelligence",
    title: "From Tools to Teammates: The Next Phase of AI Agents",
    date: "Oct 12, 2026",
    readTime: "6 min read",
    summary: "The latest generation of AI agents demonstrates unprecedented autonomy, raising questions about human-AI collaboration models.",
    slug: "tools-to-teammates",
    image: SLUG_COVERS["tools-to-teammates"],
  },
  {
    category: "Economy",
    title: "The New Geography of Opportunity in the Age of AI",
    date: "Oct 10, 2026",
    readTime: "5 min read",
    summary: "Remote work, AI infrastructure and digital economy shifts are fundamentally redrawing the map of economic opportunity.",
    slug: "geography-of-opportunity",
    image: SLUG_COVERS["geography-of-opportunity"],
  },
];

const API = process.env.REACT_APP_BACKEND_URL;
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const readTime = (a) => `${Math.max(1, Math.round([a.what_happened, a.why_it_matters, a.raulvins_signal, a.body].join(" ").split(/\s+/).filter(Boolean).length / 220))} min read`;

const fromCms = (a) => ({
  category: a.category || "Artificial Intelligence",
  title: a.title,
  date: formatDate(a.published_date),
  readTime: readTime(a),
  summary: a.excerpt || a.subtitle || a.what_happened,
  slug: a.slug,
  image: coverFor(a),
  autoImage: !a.featured_image,
});

const FutureSignals = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [cms, setCms] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/articles?limit=6`).then((r) => (r.ok ? r.json() : [])).then((d) => setCms(d.map(fromCms))).catch(() => {});
  }, []);

  const cmsSlugs = new Set(cms.map((a) => a.slug));
  const cards = [...cms, ...signals.filter((s) => !cmsSlugs.has(s.slug))].slice(0, 6);
  const used = new Set(cards.filter((c) => !c.autoImage).map((c) => c.image));
  cards.forEach((c, i) => {
    if (!c.autoImage) return;
    const pool = CATEGORY_IMAGES[c.category] || ALL_IMAGES;
    const start = hash(c.slug || c.title) % pool.length;
    const fresh = [...pool.slice(start), ...pool.slice(0, start), ...ALL_IMAGES].find((x) => !used.has(x));
    if (fresh) cards[i] = { ...c, image: fresh };
    used.add(cards[i].image);
  });

  return (
    <section ref={ref} className="py-20 md:py-28 relative" data-testid="future-signals-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-end justify-between mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white italic mb-3" data-testid="signals-heading">
              Today's Future Signals
            </h2>
            <p className="text-[var(--text-secondary)] max-w-xl text-sm md:text-base">
              The developments in AI, work, education, and human opportunity that matter now.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
          >
            <Link
              to="/intelligence"
              className="hidden md:flex items-center gap-2 text-[var(--accent-violet-light)] hover:text-white transition-colors font-medium text-sm"
              data-testid="view-all-signals"
            >
              View All Signals
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((signal, i) => (
            <motion.div
              key={signal.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={`/intelligence/${signal.slug}`}
                className="block h-full bg-[var(--bg-card)] border border-[rgba(124,58,237,0.08)] rounded-xl overflow-hidden card-hover group"
                data-testid={`signal-card-${i}`}
              >
                {/* Image */}
                <div className="h-44 relative overflow-hidden">
                  <img
                    src={signal.image}
                    alt={signal.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] via-transparent to-transparent opacity-60" />
                  <div className="absolute top-3 left-3">
                    <span className={`category-badge ${categoryColors[signal.category] || "bg-violet-500/90 text-white"}`}>
                      {signal.category}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold text-white mb-2 leading-snug group-hover:text-[var(--accent-violet-light)] transition-colors">
                    {signal.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)] mb-3">
                    <span>{signal.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {signal.readTime}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-2">
                    {signal.summary}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-[var(--accent-violet-light)] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    Read Signal <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 md:hidden text-center">
          <Link
            to="/intelligence"
            className="inline-flex items-center gap-2 text-[var(--accent-violet-light)] font-medium text-sm"
          >
            View All Signals <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FutureSignals;
